// =====================
// Dual-side budgets (Phase 3 of the budgeting rework)
// =====================
//
// A budget carries a fee side (what the client pays) and a cost side (what we
// spend), either alone valid; hours are an optional expression of the cost
// side, not a budget type. The legacy originalBudget / currentForecast /
// budgetType (entity: baselineAmount) columns stay dual-written from the sides
// until every reader migrates: fee wins when both sides are set, else
// whichever side is set.

import { z } from 'zod'
import type { BudgetType } from './project-budget-plan3'

export type BudgetSide = 'FEE' | 'COST' | 'HOURS'
export type BudgetLifecycleStatus = 'DRAFT' | 'ACTIVE' | 'CLOSED'

// ---- Summary shapes (amounts are decimal strings, matching plan3 payloads) ----

/**
 * Per-side slice of a budget summary. All money fields are null when unset OR
 * masked for the viewer (`masked: true` disambiguates); render a dash, never
 * coalesce to 0.
 */
export interface BudgetSideSummary {
  original: string | null
  forecast: string | null
  actuals: string | null
  remaining: string | null
  burnRate: string | null
  projectedCompletion: string | null
  health: 'GREEN' | 'AMBER' | 'RED' | null
  /** True when the viewer may not see this side (cost side without cost visibility). */
  masked: boolean
}

export interface BudgetMarginSummary {
  /** feeForecast - costForecast; null when either side unset or masked. */
  forecast: string | null
  forecastPercent: string | null
  /** fee actuals - cost actuals; same null semantics. */
  actual: string | null
  actualPercent: string | null
}

export interface BudgetSidesSummary {
  fee: BudgetSideSummary | null
  cost: BudgetSideSummary | null
  hours: BudgetSideSummary | null
}

/** Per-side final snapshot written at close-out (the true-up). */
export interface BudgetTrueUpSide {
  side: BudgetSide
  original: string | null
  finalForecast: string | null
  actuals: string | null
  /** actuals - finalForecast (positive = over). */
  variance: string | null
}

// ---- Request bodies ----

const decimalString = z
  .union([z.string(), z.number()])
  .transform((v) => String(v))
  .refine((v) => v.trim() !== '' && !Number.isNaN(Number(v)), {
    message: 'Must be a number',
  })

const nullableDecimalString = z
  .union([decimalString, z.null()])
  .optional()

const BUDGET_TYPES = ['LABOR_COST', 'TOTAL_COST', 'HOURS', 'REVENUE'] as const

export const SetProjectBudgetSidesSchema = z
  .object({
    feeOriginal: nullableDecimalString,
    costOriginal: nullableDecimalString,
    costHoursOriginal: nullableDecimalString,
    // Legacy single-amount payload (pre-Phase-3 clients); translated to the
    // side implied by budgetType.
    originalBudget: decimalString.optional(),
    budgetType: z.enum(BUDGET_TYPES).optional(),
    budgetCurrency: z.string().length(3).optional(),
    status: z.enum(['DRAFT', 'ACTIVE']).optional(),
    reason: z.string().max(2000).optional(),
  })
  .refine(
    (v) =>
      v.feeOriginal !== undefined ||
      v.costOriginal !== undefined ||
      v.costHoursOriginal !== undefined ||
      v.originalBudget !== undefined ||
      v.status !== undefined,
    { message: 'Provide at least one budget amount' },
  )

export type SetProjectBudgetSidesInput = z.infer<
  typeof SetProjectBudgetSidesSchema
>

export const ReforecastSideSchema = z.object({
  newForecast: decimalString,
  /** Omitted = the budget's mirrored legacy side (pre-Phase-3 clients). */
  side: z.enum(['FEE', 'COST', 'HOURS']).optional(),
  reason: z.string().min(1, 'Reason is required').max(2000),
})

export type ReforecastSideInput = z.infer<typeof ReforecastSideSchema>

export const CreateEntityBudgetPeriodSchema = z
  .object({
    feeOriginal: nullableDecimalString,
    costOriginal: nullableDecimalString,
    costHoursOriginal: nullableDecimalString,
    // Legacy payload shape.
    baselineAmount: z.number().finite().optional(),
    budgetType: z.string().max(20).optional(),
    budgetCurrency: z.string().length(3).optional(),
    /** yyyy-mm-dd; explicit null clears (undefined preserves on update). */
    periodStart: z.string().nullable().optional(),
    periodEnd: z.string().nullable().optional(),
    status: z.enum(['DRAFT', 'ACTIVE']).optional(),
    notes: z.string().max(5000).nullable().optional(),
    reason: z.string().max(2000).optional(),
  })
  .refine(
    (v) =>
      v.feeOriginal !== undefined ||
      v.costOriginal !== undefined ||
      v.costHoursOriginal !== undefined ||
      v.baselineAmount !== undefined ||
      v.status !== undefined,
    { message: 'Provide at least one budget amount' },
  )

export type CreateEntityBudgetPeriodInput = z.infer<
  typeof CreateEntityBudgetPeriodSchema
>

// ---- Legacy-mirror derivation (D1) ----
//
// These helpers are the single source of truth for the dual-write rule; the
// backfill migration's SQL CASE is their twin and the parity test asserts the
// mapping never drifts.

/** The side a legacy budgetType maps to (mirrors the backfill migration). */
export function legacySideForBudgetType(budgetType: string): BudgetSide {
  if (budgetType === 'REVENUE') return 'FEE'
  if (budgetType === 'HOURS') return 'HOURS'
  return 'COST'
}

export interface BudgetSideValues {
  feeOriginal: string | number | null
  feeForecast: string | number | null
  costOriginal: string | number | null
  costForecast: string | number | null
  costHoursOriginal: string | number | null
  costHoursForecast: string | number | null
}

export interface LegacyMirror {
  side: BudgetSide | null
  budgetType: BudgetType
  /** Values for originalBudget / baselineAmount and currentForecast; null clears. */
  original: string | number | null
  forecast: string | number | null
}

/**
 * Derive the legacy mirror from the sided values. Fee wins when both dollar
 * sides are set; hours mirror only when no dollar side exists. When mirroring
 * COST, an existing LABOR_COST/TOTAL_COST budgetType is preserved (raw-SQL and
 * export readers branch on it); new cost budgets get TOTAL_COST because the
 * cost side always includes approved expenses.
 */
export function deriveLegacyMirror(
  sides: BudgetSideValues,
  existingBudgetType?: string | null,
): LegacyMirror {
  const feeSet = sides.feeOriginal != null || sides.feeForecast != null
  const costSet = sides.costOriginal != null || sides.costForecast != null
  const hoursSet =
    sides.costHoursOriginal != null || sides.costHoursForecast != null

  if (feeSet) {
    return {
      side: 'FEE',
      budgetType: 'REVENUE',
      original: sides.feeOriginal,
      forecast: sides.feeForecast,
    }
  }
  if (costSet) {
    const budgetType: BudgetType =
      existingBudgetType === 'LABOR_COST' || existingBudgetType === 'TOTAL_COST'
        ? existingBudgetType
        : 'TOTAL_COST'
    return {
      side: 'COST',
      budgetType,
      original: sides.costOriginal,
      forecast: sides.costForecast,
    }
  }
  if (hoursSet) {
    return {
      side: 'HOURS',
      budgetType: 'HOURS',
      original: sides.costHoursOriginal,
      forecast: sides.costHoursForecast,
    }
  }
  // No side set: legacy fields clear; keep a stable budgetType for the column default.
  const budgetType: BudgetType =
    existingBudgetType === 'LABOR_COST' ||
    existingBudgetType === 'TOTAL_COST' ||
    existingBudgetType === 'HOURS' ||
    existingBudgetType === 'REVENUE'
      ? existingBudgetType
      : 'LABOR_COST'
  return { side: null, budgetType, original: null, forecast: null }
}
